/**
 * Contact Income Panel (SPRINT-6B F005)
 *
 * CRUD panel for managing individual contact income entries on a deal.
 * Shows a table of income entries with add/edit/delete actions,
 * plus a totals row summarising included entries.
 *
 * States:
 * - Empty: No income entries, shows "No contact income added" with Add button
 * - Table: Shows income entries with columns and totals
 * - Modal: Add/Edit form with live annual calculation
 * - Delete: AlertDialog confirmation
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface ContactIncomeEntry {
  id: string;
  dealId: string;
  contactId: string;
  incomeType: string;
  employerSource: string | null;
  grossAmount: number;
  frequency: string;
  annualGross: number;
  shadingPercentage: number;
  shadedAnnual: number;
  includeInServicing: boolean;
  verificationStatus: string;
  verificationDate: string | null;
  evidenceNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ContactOption {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

interface FormState {
  contactId: string;
  incomeType: string;
  employerSource: string;
  grossAmount: string;
  frequency: string;
  shadingPercentage: string;
  includeInServicing: boolean;
  verificationStatus: string;
}

export interface ContactIncomePanelProps {
  dealId: string;
}

// Constants
const INCOME_TYPE_OPTIONS = [
  { value: 'payg_salary', label: 'PAYG Salary' },
  { value: 'payg_wage', label: 'PAYG Wage' },
  { value: 'self_employed', label: 'Self Employed' },
  { value: 'rental', label: 'Rental' },
  { value: 'dividend', label: 'Dividend' },
  { value: 'trust_distribution', label: 'Trust Distribution' },
  { value: 'government_benefit', label: 'Government Benefit' },
  { value: 'superannuation', label: 'Superannuation' },
  { value: 'investment', label: 'Investment' },
  { value: 'other', label: 'Other' },
];

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
];

const VERIFICATION_STATUS_OPTIONS = [
  { value: 'unverified', label: 'Unverified' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'rejected', label: 'Rejected' },
];

const FREQUENCY_MULTIPLIERS: Record<string, number> = {
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  annually: 1,
};

const INCOME_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  INCOME_TYPE_OPTIONS.map(o => [o.value, o.label])
);

const FREQUENCY_LABELS: Record<string, string> = Object.fromEntries(
  FREQUENCY_OPTIONS.map(o => [o.value, o.label])
);

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const defaultFormState: FormState = {
  contactId: '',
  incomeType: 'payg_salary',
  employerSource: '',
  grossAmount: '',
  frequency: 'monthly',
  shadingPercentage: '100',
  includeInServicing: true,
  verificationStatus: 'unverified',
};

export const ContactIncomePanel: React.FC<ContactIncomePanelProps> = ({ dealId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<ContactIncomeEntry | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<ContactIncomeEntry | null>(null);
  const [formState, setFormState] = useState<FormState>(defaultFormState);

  // Fetch contact income entries
  const { data: entries = [], isLoading } = useQuery<ContactIncomeEntry[]>({
    queryKey: [`/api/crm/deals/${dealId}/contact-income`],
    enabled: !!dealId,
  });

  const { data: deal } = useQuery<{ clientGroupId: string | null }>({
    queryKey: [`/api/crm/deals/${dealId}`],
    enabled: !!dealId,
  });
  const clientGroupId = deal?.clientGroupId;

  // Wait for the deal's group before requesting selectable contacts.
  const { data: groupContacts = [], isSuccess: contactsLoaded } = useQuery<ContactOption[]>({
    queryKey: ['/api/crm/contacts', { clientGroupId }],
    enabled: !!dealId && !!clientGroupId,
  });
  // Disabled queries can still return cached data from an unscoped picker.
  const contacts = clientGroupId ? groupContacts : [];

  useEffect(() => {
    setIsModalOpen(false);
    setEditingEntry(null);
    setDeleteEntry(null);
    setFormState(defaultFormState);
  }, [dealId, clientGroupId]);
  const retainedContacts = useQueries({
    // Only a successful scoped list can establish which linked contacts are
    // absent. Pending or failed lists must not trigger full detail reads.
    queries: Array.from(new Set((clientGroupId && contactsLoaded ? entries : []).map(entry => entry.contactId)))
      .filter(id => !contacts.some(contact => contact.id === id))
      .map(id => ({ queryKey: ['/api/crm/contacts', id] })),
  });

  // Live calculation of annual gross
  const calculatedAnnualGross = useMemo(() => {
    const gross = parseFloat(formState.grossAmount) || 0;
    const multiplier = FREQUENCY_MULTIPLIERS[formState.frequency] || 1;
    return gross * multiplier;
  }, [formState.grossAmount, formState.frequency]);

  // Live calculation of shaded annual
  const calculatedShadedAnnual = useMemo(() => {
    const shadingPct = parseFloat(formState.shadingPercentage) || 100;
    return calculatedAnnualGross * (shadingPct / 100);
  }, [calculatedAnnualGross, formState.shadingPercentage]);

  // Sync form state when editing
  useEffect(() => {
    if (editingEntry) {
      setFormState({
        contactId: editingEntry.contactId,
        incomeType: editingEntry.incomeType,
        employerSource: editingEntry.employerSource || '',
        grossAmount: String(editingEntry.grossAmount),
        frequency: editingEntry.frequency,
        shadingPercentage: String(editingEntry.shadingPercentage),
        includeInServicing: editingEntry.includeInServicing,
        verificationStatus: editingEntry.verificationStatus,
      });
    }
  }, [editingEntry]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', `/api/crm/deals/${dealId}/contact-income`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/contact-income`] });
      handleCloseModal();
      toast({ title: 'Contact income added', description: 'Income entry has been created' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create income entry', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest('PATCH', `/api/crm/deals/${dealId}/contact-income/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/contact-income`] });
      handleCloseModal();
      toast({ title: 'Contact income updated', description: 'Income entry has been updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update income entry', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/crm/deals/${dealId}/contact-income/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/contact-income`] });
      setDeleteEntry(null);
      toast({ title: 'Contact income deleted', description: 'Income entry has been removed' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete income entry', variant: 'destructive' });
    },
  });

  const handleOpenAdd = () => {
    setEditingEntry(null);
    setFormState(defaultFormState);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (entry: ContactIncomeEntry) => {
    setEditingEntry(entry);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEntry(null);
    setFormState(defaultFormState);
  };

  const handleSave = async () => {
    const payload = {
      contactId: formState.contactId,
      incomeType: formState.incomeType,
      employerSource: formState.employerSource || null,
      grossAmount: parseFloat(formState.grossAmount),
      frequency: formState.frequency,
      shadingPercentage: parseFloat(formState.shadingPercentage) || 100,
      includeInServicing: formState.includeInServicing,
      verificationStatus: formState.verificationStatus,
    };

    if (editingEntry) {
      await updateMutation.mutateAsync({ id: editingEntry.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  // Calculate totals for included entries
  const totals = useMemo(() => {
    const included = entries.filter(e => e.includeInServicing);
    return {
      totalAnnualGross: included.reduce((sum, e) => sum + Number(e.annualGross), 0),
      totalShadedAnnual: included.reduce((sum, e) => sum + Number(e.shadedAnnual), 0),
    };
  }, [entries]);

  // Get contact name by id
  const getContactName = (contactId: string): string => {
    const contact = contacts.find(c => c.id === contactId)
      ?? retainedContacts.map(result => result.data as ContactOption | undefined).find(c => c?.id === contactId);
    if (contact) {
      return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unknown';
    }
    return 'Unknown';
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="contact-income-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="contact-income-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Contact Income</CardTitle>
            {entries.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleOpenAdd}>
                <Plus className="h-4 w-4 mr-1" />
                Add Contact Income
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-muted-foreground mb-2">No contact income added</p>
              <p className="text-xs text-muted-foreground mb-6">
                Add income from contacts associated with this deal for serviceability analysis
              </p>
              <Button onClick={handleOpenAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add Contact Income
              </Button>
            </div>
          ) : (
            // Table view
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Contact</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Source</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Gross</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Frequency</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Annual Gross</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Shaded Annual</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b">
                      <td className="py-2 px-2">{getContactName(entry.contactId)}</td>
                      <td className="py-2 px-2">{INCOME_TYPE_LABELS[entry.incomeType] || entry.incomeType}</td>
                      <td className="py-2 px-2">{entry.employerSource || '-'}</td>
                      <td className="py-2 px-2 text-right">{formatCurrency(Number(entry.grossAmount))}</td>
                      <td className="py-2 px-2">{FREQUENCY_LABELS[entry.frequency] || entry.frequency}</td>
                      <td className="py-2 px-2 text-right">{formatCurrency(Number(entry.annualGross))}</td>
                      <td className="py-2 px-2 text-right">{formatCurrency(Number(entry.shadedAnnual))}</td>
                      <td className="py-2 px-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(entry)}
                            aria-label="edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteEntry(entry)}
                            aria-label="delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold" data-testid="contact-income-totals">
                    <td className="py-2 px-2" colSpan={5}>Total (Included)</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(totals.totalAnnualGross)}</td>
                    <td className="py-2 px-2 text-right">{formatCurrency(totals.totalShadedAnnual)}</td>
                    <td className="py-2 px-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEntry ? 'Edit Contact Income' : 'Add Contact Income'}</DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Update the income details below' : 'Enter income details for a contact'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Contact select */}
            <div className="space-y-2">
              <Label htmlFor="contactId">Contact</Label>
              <select
                id="contactId"
                name="contactId"
                value={formState.contactId}
                onChange={(e) => setFormState(prev => ({ ...prev, contactId: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="">Select a contact...</option>
                {formState.contactId && !contacts.some(contact => contact.id === formState.contactId) && (
                  <option value={formState.contactId} disabled>{getContactName(formState.contactId)}</option>
                )}
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                  </option>
                ))}
              </select>
            </div>

            {/* Income Type select */}
            <div className="space-y-2">
              <Label htmlFor="incomeType">Income Type</Label>
              <select
                id="incomeType"
                name="incomeType"
                value={formState.incomeType}
                onChange={(e) => setFormState(prev => ({ ...prev, incomeType: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {INCOME_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Employer/Source */}
            <div className="space-y-2">
              <Label htmlFor="employerSource">Employer / Source</Label>
              <Input
                id="employerSource"
                name="employerSource"
                value={formState.employerSource}
                onChange={(e) => setFormState(prev => ({ ...prev, employerSource: e.target.value }))}
                placeholder="e.g., ABC Corporation"
              />
            </div>

            {/* Gross Amount */}
            <div className="space-y-2">
              <Label htmlFor="grossAmount">Gross Amount ($)</Label>
              <Input
                id="grossAmount"
                name="grossAmount"
                data-testid="gross-amount-input"
                type="number"
                step="0.01"
                min="0"
                value={formState.grossAmount}
                onChange={(e) => setFormState(prev => ({ ...prev, grossAmount: e.target.value }))}
                placeholder="e.g., 10000"
              />
            </div>

            {/* Frequency */}
            <div className="space-y-2">
              <Label htmlFor="frequency">Frequency</Label>
              <select
                id="frequency"
                name="frequency"
                data-testid="frequency-select"
                value={formState.frequency}
                onChange={(e) => setFormState(prev => ({ ...prev, frequency: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Annual Gross (read-only, auto-calculated) */}
            <div className="space-y-2">
              <Label>Annual Gross</Label>
              <div
                data-testid="annual-gross-display"
                className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm font-medium"
              >
                {formatCurrency(calculatedAnnualGross)}
              </div>
            </div>

            {/* Shading Percentage */}
            <div className="space-y-2">
              <Label htmlFor="shadingPercentage">Shading Percentage (%)</Label>
              <Input
                id="shadingPercentage"
                name="shadingPercentage"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formState.shadingPercentage}
                onChange={(e) => setFormState(prev => ({ ...prev, shadingPercentage: e.target.value }))}
                placeholder="100"
              />
            </div>

            {/* Shaded Annual (read-only) */}
            <div className="space-y-2">
              <Label>Shaded Annual</Label>
              <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm font-medium">
                {formatCurrency(calculatedShadedAnnual)}
              </div>
            </div>

            {/* Verification Status */}
            <div className="space-y-2">
              <Label htmlFor="verificationStatus">Verification Status</Label>
              <select
                id="verificationStatus"
                name="verificationStatus"
                value={formState.verificationStatus}
                onChange={(e) => setFormState(prev => ({ ...prev, verificationStatus: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {VERIFICATION_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Include in Servicing toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="includeInServicing">Include in Servicing</Label>
              <Switch
                id="includeInServicing"
                checked={formState.includeInServicing}
                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, includeInServicing: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : editingEntry ? (
                'Save'
              ) : (
                'Add Income'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteEntry} onOpenChange={(open) => !open && setDeleteEntry(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact Income</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this income entry? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteEntry && deleteMutation.mutate(deleteEntry.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ContactIncomePanel;
